import { Trophy, Zap, Target } from 'lucide-react';

// Open Play podium rules. Each session keeps the rule it was created (or
// re-crowned) under:
//   2 — fair podium (owner 2026-09-30): (wins + 1) ÷ (games + 2), then share of points.
//   3 — wins podium (owner 2026-10-04): most wins, then the better win rate; the
//       same record shares a place; needs 60% of the busiest player's games AND
//       more wins than losses. Points are out (weaker players get shuffled in).
// Both count real games only — walkovers are shown apart.
export const isFairRule = (x) => x?.podium_rule === 2 || x?.podium_rule === 3;
export const isWinsRule = (x) => x?.podium_rule === 3;

// "2–1 + W/O win · 57% of points" (rule 2) / "4–2 · 67%" (rule 3).
export function fairRecord(p, { points = true } = {}) {
  const wo = [];
  if (p.walkover_wins) wo.push(`${p.walkover_wins > 1 ? p.walkover_wins + ' ' : ''}W/O win${p.walkover_wins > 1 ? 's' : ''}`);
  if (p.walkover_losses) wo.push(`${p.walkover_losses > 1 ? p.walkover_losses + ' ' : ''}W/O loss${p.walkover_losses > 1 ? 'es' : ''}`);
  const tail = 'podium_reason' in p ? ` · ${p.win_rate}%`
    : (points && p.points_ratio != null ? ` · ${p.points_ratio}% of points` : '');
  return `${p.wins}–${p.losses}${wo.length ? ` + ${wo.join(', ')}` : ''}${tail}`;
}

// Why an unranked player is off the podium, in a few words.
export function unrankedWhy(p, minGames) {
  return p.podium_reason === 'record' ? 'needs more wins than losses' : `under ${minGames} games`;
}

export function HowRanked({ minGames, rule = 2, tone = '#D6C08A' }) {
  return (
    <p data-testid="op-how-ranked" className="text-[11px] leading-relaxed text-center"
      style={{ color: '#8A8A8A' }}>
      <span style={{ color: tone, fontWeight: 700 }}>How it's ranked: </span>
      {rule === 3 ? <>
        most wins, then the better win rate. The same record shares a place. Walkovers don't count.
        Podium needs at least <b style={{ color: '#E5E5E5' }}>{minGames} games</b> and more wins than losses.
      </> : <>
        win rate with a small bonus for playing more games, then the share of points won.
        Walkovers don't count. Podium needs at least <b style={{ color: '#E5E5E5' }}>{minGames} games</b>.
      </>}
    </p>
  );
}

const ICONS = { most_wins: Trophy, iron_player: Zap, best_win_rate: Target, best_points_ratio: Target };

// Full-size award tiles (console podium + public podium). `compact` = one
// line per award for the public events list strip.
export default function OpenPlayAwards({ awards, compact = false, muted = '#8A8A8A' }) {
  if (!awards || awards.length === 0) return null;
  if (compact) {
    return (
      <div data-testid="op-awards-compact" style={{ display: 'grid', gap: 4, marginTop: 8 }}>
        {awards.map(a => {
          const Icon = ICONS[a.key] || Trophy;
          return (
            <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, minWidth: 0 }}>
              <Icon style={{ width: 12, height: 12, color: '#FFD54A', flex: 'none' }} />
              <span style={{ color: '#D6C08A', fontWeight: 700, whiteSpace: 'nowrap' }}>{a.label}</span>
              <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
              <span style={{ color: muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {a.detail}</span>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div data-testid="op-awards">
      <p className="text-center text-xs font-bold uppercase mb-2.5"
        style={{ color: '#D6C08A', fontFamily: 'Oswald, sans-serif', letterSpacing: '0.2em' }}>Awards</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {awards.map(a => {
          const Icon = ICONS[a.key] || Trophy;
          return (
            <div key={a.key} data-testid={`op-award-${a.key}`}
              className="rounded-xl px-3 py-3 flex sm:flex-col items-center sm:text-center gap-3 sm:gap-1.5"
              style={{ background: '#0F0F0F', border: '1px solid #2A2410' }}>
              <span className="rounded-lg flex items-center justify-center shrink-0"
                style={{ width: 34, height: 34, background: '#FFD54A14', border: '1px solid #FFD54A33' }}>
                <Icon className="h-4 w-4" style={{ color: '#FFD54A' }} />
              </span>
              <span className="min-w-0">
                <span className="block text-[10px] font-extrabold uppercase" style={{ color: '#D6C08A', letterSpacing: '0.1em' }}>{a.label}</span>
                <span className="block text-sm font-extrabold truncate" style={{ color: '#fff' }}>{a.name}</span>
                <span className="block text-[11px]" style={{ color: '#8A8A8A' }}>{a.detail}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Awards in the champions-card payload (the card draws label / name / detail).
export const cardAwards = (awards) => (awards || []).map(a => ({ label: a.label, name: a.name, detail: a.detail }));
